import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommentApiService } from '@shared/api/api-loop/services/comment-api.service';
import { SmartConversationsApiService } from '@shared/modules/conversations/services/conversations-api-cache/smart-conversations-api.service';
import { MailCommentModule } from '@shared/modules/comments/components/mail-comment/mail-comment.module';
import { CommentTypeModule } from '@shared/modules/comments/components/pipes/comment-type/comment-type.module';
import { IsChatCommentPipe } from '@shared/modules/comments/components/pipes/comment-type/is-chat-comment.pipe';
import { ChatCommentModule } from '@shared/modules/comments/components/chat-comment/chat-comment.module';
import { ToastService } from '@shared/ui/kit/toast/toast.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { CommentBaseModel, CommentMailModel, CommentModel } from '@dta/shared/models-api-loop/comment/comment.model';
import { ConversationModel } from '@dta/shared/models-api-loop/conversation-card/conversation/conversation.model';
import { KeyboardActionModeType } from '@dta/shared/models/keyboard-shortcut.model';
import { KeyboardShortcutsListenerService } from '@dta/ui/services/keyboard-shortcuts-listener/keyboard-shortcuts-listener.service';
import { writeTextToClipboard } from '@dta/shared/clipboard/write-text-to-clipboard';

@Component({
  selector: 'loop-dynamic-dialog-comment',
  template: `
    <div class="flex flex-col padding h-100 gap-half overflow-auto-y">
      @let conversation = conversationSignal();
      @let comment = commentSignal();
      @if (conversation && comment) {
        @if (comment | isMailComment) {
          <div
            class="dynamic-dialog-comment-subject text selectable bold"
            (dblclick)="copyTitleToClipboard(conversation)"
          >
            {{ conversation.name || '\\<No subject\\>' }}
          </div>
          <loop-mail-comment class="h-100" [conversation]="conversation" [comment]="comment" />
        } @else if (comment | isChatComment) {
          <loop-chat-comment
            [isCollapsed]="false"
            [previousComment]="null"
            [comment]="comment"
            [conversationId]="conversation.cardId"
          />
        }
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .dynamic-dialog-comment-subject {
      padding-bottom: var(--theme-spacing-half);
      font-size: var(--font-size-x-large);
      user-select: text;
      cursor: text;
    }
  `,
  imports: [MailCommentModule, CommentTypeModule, IsChatCommentPipe, ChatCommentModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class DynamicDialogCommentComponent implements OnInit {
  private readonly commentApiService: CommentApiService = inject(CommentApiService);
  private readonly smartConversationsApiService: SmartConversationsApiService = inject(SmartConversationsApiService);
  private readonly keyboardShortcutsListenerService: KeyboardShortcutsListenerService = inject(
    KeyboardShortcutsListenerService
  );
  private readonly toastService: ToastService = inject(ToastService);

  private readonly commentIdSignal: WritableSignal<string | null> = signal(null);
  @Input() set commentId(commentId: string) {
    this.commentIdSignal.set(commentId);
  }

  protected readonly commentSignal: Signal<CommentModel | null> = toSignal(
    toObservable(this.commentIdSignal).pipe(
      filter((commentId: string | null): commentId is string => !!commentId),
      switchMap(commentId => {
        return this.commentApiService.Comment_Get({
          id: commentId
        });
      }),
      map(comment => {
        const commentModel = CommentBaseModel.create(comment);
        // Set for shortcut actions
        if (commentModel instanceof CommentMailModel) {
          commentModel.isLastMailComment = true;
        }
        return commentModel;
      })
    ),
    { initialValue: null }
  );

  protected readonly conversationSignal: Signal<ConversationModel | null> = toSignal(
    toObservable(this.commentSignal).pipe(
      filter((comment: CommentMailModel | null): comment is CommentMailModel => !!comment),
      switchMap(comment => {
        return this.smartConversationsApiService.getSingle(comment.parent.id.replace('-copy1T', ''));
      })
    ),
    { initialValue: null }
  );

  ngOnInit(): void {
    this.keyboardShortcutsListenerService.setMode(KeyboardActionModeType.THREAD);
  }

  protected copyTitleToClipboard(conversation: ConversationModel): void {
    writeTextToClipboard(conversation.name);

    this.toastService.info({ title: 'Title copied to clipboard' });
  }
}
